import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { useMediaQuery } from 'hooks/useMediaQuery';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Tab } from '@components/common/Tab';
import { Container } from '@components/parts/Container';

import { UploadedImage } from '@customTypes/UploadedImage';

type Props = {
  tabs: {
    name: string;
    image?: UploadedImage;
    mobileImage?: UploadedImage;
    coverages: string;
    disabled: boolean;
    table?: string;
    hint?: string;
  }[];
};

const ActiveTab = styled.div`
  margin-bottom: 90px;
  min-height: 490px;
  @media (min-width: 992px) {
    margin-bottom: 160px;
    min-height: 560px;
  }
  .image {
    width: 100%;
    height: 100%;
    min-height: 200px;
    margin-top: 40px;
    @media (max-width: 992px) {
      margin-top: 50px;
    }
  }
  .coverages {
    flex-direction: column;
    margin-top: 50px;
    display: flex;
    .name {
      flex: 1;
      margin-bottom: 0.5em;
      h4 {
        @media (min-width: 992px) {
          font-size: 36px;
        }
      }
    }
    .content {
      flex: 2;
      .list {
        flex-direction: row;
        flex-wrap: wrap;
        .item {
          .align-center {
            .icon {
              min-width: 30px;
              margin-right: 0.4em;
            }
            font-size: 1.1em;
            padding: 0.5em 0.5em 0.5em 0;

            font-weight: 500;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            width: 100%;
          }
        }
      }
      .hint-container {
        .hint {
          display: flex;
          align-items: flex-start;
          width: 100%;
          margin-top: 30px;
          border-top: 1px solid #e3e5ea;
          font-size: 15px;
          color: #62656a;
          padding: 30px 0em 0.5em 0em;
          .icon {
            margin-right: 0.3em;
          }
          p {
            margin-top: 0;
          }
        }
      }
      .table-container {
        @media (min-width: 992px) {
          max-width: 540px;
          margin-left: auto;
        }
        flex: 2;
        table {
          margin-top: 16px;
          @media (min-width: 992px) {
            max-width: 540px;
            margin-top: 0;
          }
          border-collapse: collapse;
          width: 100%;
          font-size: 1rem;
          border-spacing: 0;
          th {
            border: 1px solid #e3e5ea;
            padding: 14px;
            &:first-child {
              text-align: left;
              padding: 14px 14px 14px 30px;
            }
            font-weight: 500;
            background: #f3f4f8;
            color: #62656a;
            text-align: center;
          }
          td {
            color: #2e3035;
            border: 1px solid #e3e5ea;
            font-weight: 500;
            text-align: center;
            padding: 30px 14px;
            &:first-child {
              text-align: left;
              padding: 30px 14px 30px 30px;
            }
            strong {
              color: #055cf5;
            }
            em {
              display: block;
              font-weight: 400;
              font-style: normal;
            }
            img {
              height: 60px;
              @media (min-width: 992px) {
                margin-left: auto;
              }
              margin-right: auto;
              display: block;
              vertical-align: middle;
              @media (min-width: 992px) {
                margin-left: inherit;
                margin-right: inherit;
                display: inline;
                height: 70px;
                margin-right: 20px;
              }
            }
          }
        }
        p {
          margin-top: 24px;
          color: #a3a7ae;
          font-size: 14px;
          font-weight: 500;
          @media (min-width: 992px) {
            font-size: 16px;
          }
        }
      }
    }

    @media (min-width: 992px) {
      flex-direction: row;
      .list {
        display: block;
        column-count: 2;
        .item {
          display: block;
          width: 100%;
          break-inside: avoid;
        }
      }
    }
  }
`;

const CheckIcon = () => (
  <svg className="icon" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#EFF7FF" />
    <path d="M10 15.251L13.597 19.0481L20.7143 11.4277" stroke="#055CF5" strokeWidth="2" strokeLinecap="square" />
  </svg>
);

const DisabledIcon = () => (
  <svg className="icon" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="15" cy="15" r="15" fill="#F3F4F8" />
    <path d="M10 20L15 15.0459L10 10" stroke="#A3A7AE" strokeWidth="2" />
    <path d="M20 10L15 14.9541L20 20" stroke="#A3A7AE" strokeWidth="2" />
  </svg>
);

const InfoIcon = () => (
  <svg className="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 12C4 7.60228 7.60228 4 12 4C16.3977 4 20 7.60228 20 12C20 16.3977 16.3977 20 12 20C7.60228 20 4 16.3977 4 12Z"
      stroke="#055CF5"
      strokeWidth="2"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.294 8.147C13.294 8.78 12.78 9.294 12.147 9.294C11.513 9.294 11 8.78 11 8.147C11 7.514 11.513 7 12.147 7C12.78 7 13.294 7.514 13.294 8.147Z"
      fill="#055CF5"
    />
    <path d="M12.665 17.1289V10.8789H12.165H11.665V17.1289H12.165H12.665Z" fill="#055CF5" stroke="#055CF5" />
  </svg>
);

export const ServiceScope: React.FC<Props> = (props) => {
  const { tabs } = props;
  const [currentTab, setCurrentTab] = useState(0);
  const active = tabs[currentTab];

  const isMobile = useMediaQuery('(max-width: 430px)');

  return (
    <Container>
      <ScrollReveal>
        <Tab>
          {tabs.map((tab, idx) => (
            <div
              key={idx}
              className={`${idx === currentTab ? 'active ' : ''}item`}
              onClick={(e) => {
                setCurrentTab(idx);
                try {
                  // @ts-ignore
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                } catch (e) {}
              }}
            >
              <h5>{tab.name}</h5>
            </div>
          ))}
        </Tab>
      </ScrollReveal>
      <ActiveTab>
        {active.image?.data?.attributes?.url ? (
          <ScrollReveal key={active.name}>
            <img
              className="image"
              src={isMobile ? active.mobileImage?.data.attributes.url ?? '' : active.image.data.attributes.url}
              alt={active.name}
            />
          </ScrollReveal>
        ) : null}
        <div className="coverages">
          <ScrollReveal className="name" key={active.name}>
            <h4>{active.name}</h4>
          </ScrollReveal>
          <div className="content">
            <div className="list">
              {(active.coverages || '')
                .trim()
                .split('\n')
                .filter((a) => a)
                .map((coverage, i) => (
                  <ScrollReveal key={`${active.name}-${i}`} className="item" intervalDelay={(i + 1) * 100}>
                    <div className="align-center">
                      {active.disabled ? <DisabledIcon /> : <CheckIcon />}
                      {coverage}
                    </div>
                  </ScrollReveal>
                ))}
            </div>
            <ScrollReveal key={`${active.name}-table`} className="table-container" style={{ width: '100%' }}>
              {active.table ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {active.table}
                </ReactMarkdown>
              ) : null}
            </ScrollReveal>
            {active.hint ? (
              <ScrollReveal className="hint-container">
                <div className="hint">
                  <InfoIcon />
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {active.hint}
                  </ReactMarkdown>
                </div>
              </ScrollReveal>
            ) : null}
          </div>
        </div>
      </ActiveTab>
    </Container>
  );
};
